// src/components/dashboard/table-planner/TableCard.jsx
import React, { useState, forwardRef } from "react";
import SeatedGuest from "./SeatedGuest";
const TableCard = forwardRef(
  (
    {
      table,
      onDrop,
      onDragOver,
      onUpdateTableName,
      onUpdateTableCapacity,
      onDeleteTable,
      onRemoveGuest,
      selectedGuest,
      onAssignGuest,
      onRearrangeTables,
    },
    ref
  ) => {
    const [isEditingName, setIsEditingName] = useState(false);
    const [isEditingCapacity, setIsEditingCapacity] = useState(false);
    const [isTableDragOver, setTableDragOver] = useState(false);

    const currentPax = table.guests.reduce((sum, g) => sum + g.pax, 0);
    const canAddSelectedGuest = selectedGuest
      ? currentPax + selectedGuest.pax <= table.capacity
      : false;

    const handleTableDragStart = (e) => {
      e.dataTransfer.setData("type", "table");
      e.dataTransfer.setData("tableId", table.id);
      e.dataTransfer.effectAllowed = "move";
    };

    const handleCombinedDrop = (e) => {
      setTableDragOver(false);
      const type = e.dataTransfer.getData("type");

      if (type === "table") {
        const draggedTableId = e.dataTransfer.getData("tableId");
        if (draggedTableId !== table.id) {
          onRearrangeTables(draggedTableId, table.id);
        }
      } else if (type === "guest") {
        onDrop(e, table.id);
      }
    };

    return (
      <div
        ref={ref}
        draggable={true}
        onDragStart={handleTableDragStart}
        onDragOver={(e) => {
          e.preventDefault();
          if (e.dataTransfer.getData("type") === "table") {
            setTableDragOver(true);
          }
        }}
        onDragLeave={() => setTableDragOver(false)}
        onDrop={handleCombinedDrop}
        className={`bg-white/80 p-4 rounded-xl shadow-lg backdrop-blur-sm min-h-[200px] relative transition-all duration-200 ${
          isTableDragOver ? "ring-2 ring-blue-500 ring-offset-2" : ""
        }`}
      >
        {selectedGuest && (
          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 z-10 flex items-center justify-center rounded-xl p-4">
            <button
              onClick={() => onAssignGuest(table.id)}
              disabled={!canAddSelectedGuest}
              className="px-6 py-3 bg-white text-blue-700 font-bold rounded-lg shadow-lg hover:bg-blue-50 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {canAddSelectedGuest ? "Add Guest Here" : "Table Full"}
            </button>
          </div>
        )}
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <div className="flex items-center gap-3 flex-grow min-w-0">
            <span className="flex-shrink-0 bg-[#931b1c] text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-md">
              {table.tableNumber}
            </span>
            <div className="flex-grow ">
              {isEditingName ? (
                <input
                  type="text"
                  value={table.name}
                  autoFocus
                  className="text-lg font-bold text-[#931b1c] bg-transparent border-b-2 border-[#931b1c] focus:outline-none w-full"
                  onChange={(e) => onUpdateTableName(table.id, e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setIsEditingName(false)
                  }
                />
              ) : (
                <h3
                  onClick={() => setIsEditingName(true)}
                  className={`w-full text-lg font-bold cursor-pointer text-wrap truncate ${
                    table.name ? "text-[#931b1c]" : "text-gray-400"
                  }`}
                  title={table.name || "Click to add a table name"}
                >
                  {table.name || "[Click to name table]"}
                </h3>
              )}
            </div>
          </div>
          <div className="flex items-center flex-shrink-0 ml-2">
            {isEditingCapacity ? (
              <input
                type="number"
                value={table.capacity}
                autoFocus
                className="w-16 text-right font-semibold px-2 py-1 text-sm rounded-md border border-gray-300"
                onChange={(e) =>
                  onUpdateTableCapacity(table.id, parseInt(e.target.value, 10))
                }
                onBlur={() => setIsEditingCapacity(false)}
                onKeyDown={(e) =>
                  e.key === "Enter" && setIsEditingCapacity(false)
                }
              />
            ) : (
              <span
                onClick={() => setIsEditingCapacity(true)}
                className={`font-semibold px-3 py-1 text-sm rounded-full cursor-pointer ${
                  currentPax >= table.capacity
                    ? "bg-red-200 text-red-800"
                    : "bg-blue-200 text-blue-800"
                }`}
                title="Click to edit capacity"
              >
                {currentPax} / {table.capacity}
              </span>
            )}
            <button
              onClick={() => onDeleteTable(table.id)}
              className="ml-3 text-gray-500 hover:text-red-700"
              title="Delete Table"
            >
              <svg
                viewBox="0 -0.5 21 21"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-5 w-5 stroke-current"
              >
                <g id="Page-1" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g
                    id="Dribbble-Light-Preview"
                    transform="translate(-179.000000, -360.000000)"
                    fill="#000000"
                  >
                    <g id="icons" transform="translate(56.000000, 160.000000)">
                      <path
                        d="M130.35,216 L132.45,216 L132.45,208 L130.35,208 L130.35,216 Z M134.55,216 L136.65,216 L136.65,208 L134.55,208 L134.55,216 Z M128.25,218 L138.75,218 L138.75,206 L128.25,206 L128.25,218 Z M130.35,204 L136.65,204 L136.65,202 L130.35,202 L130.35,204 Z M138.75,204 L138.75,200 L128.25,200 L128.25,204 L123,204 L123,206 L126.15,206 L126.15,220 L140.85,220 L140.85,206 L144,206 L144,204 L138.75,204 Z"
                        id="delete-[#1487]"
                      ></path>
                    </g>
                  </g>
                </g>
              </svg>
            </button>
          </div>
        </div>
        <div className="space-y-2">
          {table.guests.map((guest) => (
            <SeatedGuest
              key={guest.id}
              guest={guest}
              onRemove={() => onRemoveGuest(guest, table.id)}
            />
          ))}
        </div>
      </div>
    );
  }
);

export default TableCard;
